import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Tag, Space, Button, Tooltip } from 'antd'
import { SortOrder } from 'antd/lib/table/interface'

import ProfileAvatar from '../../../helpers/ProfileAvatar/ProfileAvatar'

import { User, Statuses } from '../../../../context/types'
import ChangeExpertStatusModal from './ChangeExpertStatusModal'
import { WarningFilled } from '@ant-design/icons'

type Props = {
	onUpdateStatus: (isUpdated: boolean) => void
}

export const columns = ({ onUpdateStatus }: Props) => [
	{
		title: 'First Name',
		dataIndex: 'first_name',
		key: 'first_name',
		defaultSortOrder: 'descend' as SortOrder,
		sortDirections: ['descend', 'ascend'] as SortOrder[],
		sorter: () => 0,
		render: (text: string, v: User) => (
			<span>
				<ProfileAvatar className="header-avatar" style={{ marginRight: 10 }} size="large" src={v.profile_pic_url} />
				<b>{text}</b>
			</span>
		),
	},
	{
		title: 'Last Name',
		dataIndex: 'last_name',
		key: 'last_name',
		defaultSortOrder: 'descend' as SortOrder,
		sortDirections: ['descend', 'ascend'] as SortOrder[],
		sorter: () => 0,
		render: (text: string, v: User) => (
			<span>
				{v.slug ? (
					<Link to={`/expert/${v.slug}`} target="_blank">
						{text}
					</Link>
				) : (
					<b>{text}</b>
				)}
			</span>
		),
	},
	{
		title: 'Email',
		dataIndex: 'email',
		key: 'email',
		defaultSortOrder: 'descend' as SortOrder,
		sortDirections: ['descend', 'ascend'] as SortOrder[],
		sorter: () => 0,
		render: (text: string) => (
			<span>
				<a href={`mailto:${text}`}>{text}</a>
			</span>
		),
	},
	{
		title: 'Is Expert',
		dataIndex: 'isExpert',
		key: 'isExpert',
		defaultSortOrder: 'descend' as SortOrder,
		sortDirections: ['descend', 'ascend'] as SortOrder[],
		sorter: () => 0,
		render: (v: boolean, user: User) => {
			const isExpert = parseInt(user.profiles_count, 10) > 0
			return <Tag color={isExpert ? 'geekblue' : 'volcano'}>{isExpert ? 'Yes' : 'No'}</Tag>
		},
	},
	{
		title: 'Brand',
		dataIndex: 'brand',
		key: 'brand.name',
		defaultSortOrder: 'descend' as SortOrder,
		sortDirections: ['descend', 'ascend'] as SortOrder[],
		sorter: () => 0,
		render: (brand: string) => {
			return <span>{brand || '-'}</span>
		},
	},
	{
		title: 'Channel List',
		dataIndex: 'channel_names',
		key: 'channelNames',
		render: (channelNames: string[]) => <span>{channelNames.join(', ')}</span>,
	},
	{
		title: 'Is Subscribed ',
		dataIndex: 'isSubscribed',
		key: 'isSubscribed',
		defaultSortOrder: 'descend' as SortOrder,
		sortDirections: ['descend', 'ascend'] as SortOrder[],
		sorter: () => 0,
		render: (v: boolean, user: User) => {
			const isSubscribed = parseInt(user.subscriptions_count, 10) > 0
			return <Tag color={isSubscribed ? 'geekblue' : 'volcano'}>{isSubscribed ? 'Yes' : 'No'}</Tag>
		},
	},
	{
		title: 'Status',
		dataIndex: 'status',
		key: 'status',
		defaultSortOrder: 'descend' as SortOrder,
		sortDirections: ['descend', 'ascend'] as SortOrder[],
		sorter: () => 0,
		render: (text: string, user: User) => {
			const isExpert = parseInt(user.profiles_count, 10) > 0
			const [isModalVisible, setModalVisible] = useState(false)
			const status = isExpert && Object.keys(Statuses)[Object.values(Statuses).indexOf(text as Statuses)]

			return (
				<Space size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
					{!isExpert ? (
						<Link to={`/profile/admin/upgrade/${user.id}`}>Upgrade</Link>
					) : (
						<>
							<Button style={{ padding: 0 }} type="link" onClick={() => setModalVisible(true)}>
								<Tag>{status}</Tag>
							</Button>
							{!user.is_profile_completed && (
								<Tooltip title="This expert profile is incomplete">
									<WarningFilled style={{ color: '#ffd400', fontSize: 20 }} />
								</Tooltip>
							)}
							<ChangeExpertStatusModal
								user={user}
								isVisible={isModalVisible}
								onChange={setModalVisible}
								onSave={onUpdateStatus}
							/>
						</>
					)}
				</Space>
			)
		},
	},
]
